import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Select, type SelectOption } from "@/components/Select";
import { Textarea } from "@/components/Textarea";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { Icon } from "@/components/Icon";
import { Alert } from "@/components/Alert";
import { Markdown } from "@/components/Markdown";
import { useToast } from "@/context/ToastContext";
import { aiToolsApi } from "@/lib/api";

export interface UserStoriesModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface UserStoriesResult {
    sessionId?: string;
    status?: string;
    command?: string;
    result?: string;
    llmModel?: string;
}

const DEFAULT_COMMAND = "sincronizar-sesion-desde-repo:v1";
const DEFAULT_PROMPT = "Lístame las historias de usuario por áreas";

const FALLBACK_COMMANDS: string[] = [
    "sincronizar-sesion-desde-repo:v1",
    "entrevista-historias-usuario:v4",
    "entrevista-historias-usuario:v3",
    "historia-usuario-openspec:v1",
    "informe-estado-desarrollo:v1",
    "informe-modelo-hu:v1",
    "openapi-contratos:v1",
    "prototipo-html-hu:v1",
    "prototipo-spa-aplicativo:v1",
    "publicar-modelo-requerimientos:v1",
    "publicar-prototipo-spa:v1",
    "repo-entregables:v1",
    "sincronizar-prototipo-spa-desde-repo:v1",
    "verificar-hu-invest:v1",
    "verificar-hu-secops:v1",
    "verificar-modelo-datos:v1",
];

export const UserStoriesModal: React.FC<UserStoriesModalProps> = ({
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [command, setCommand] = useState<string>(DEFAULT_COMMAND);
    const [commandOptions, setCommandOptions] = useState<
        SelectOption<string>[]
    >(
        FALLBACK_COMMANDS.map((cmd: string) => ({
            value: cmd,
            label: cmd,
        }))
    );
    const [prompt, setPrompt] = useState<string>(DEFAULT_PROMPT);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isPolling, setIsPolling] = useState<boolean>(false);
    const [pollCount, setPollCount] = useState<number>(0);
    const [resultData, setResultData] = useState<UserStoriesResult | null>(
        null
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [copied, setCopied] = useState<boolean>(false);

    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const clearPolling = (): void => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    useEffect(() => {
        return () => {
            clearPolling();
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            const loadCommands = async (): Promise<void> => {
                try {
                    const res = await aiToolsApi.getCommands();
                    const list: string[] = res?.data || FALLBACK_COMMANDS;
                    if (Array.isArray(list) && list.length > 0) {
                        setCommandOptions(
                            list.map((cmd: string) => ({
                                value: cmd,
                                label: cmd,
                            }))
                        );
                    }
                } catch (e: unknown) {
                    // Keep fallback commands if local AI is unreachable
                }
            };
            loadCommands();
        } else {
            clearPolling();
            setIsLoading(false);
            setIsPolling(false);
        }
    }, [isOpen]);

    const checkSessionStatus = async (sessionId: string): Promise<void> => {
        setPollCount((prev: number) => prev + 1);

        try {
            const res = await aiToolsApi.fetchUserStories({
                session_id: sessionId,
            });

            const data: UserStoriesResult | undefined = res?.data;

            if (data) {
                setResultData(data);

                if (data.status === "COMPLETED") {
                    clearPolling();
                    setIsPolling(false);
                    setIsLoading(false);
                    if (res?.message) {
                        showSuccess(res.message);
                    }
                } else {
                    if (
                        data.status === "FAILED" ||
                        data.status === "CANCELLED" ||
                        data.status === "TIMEOUT"
                    ) {
                        clearPolling();
                        setIsPolling(false);
                        setIsLoading(false);
                        const err =
                            res?.message ||
                            t("tools.user_stories.error_occurred");
                        setErrorMessage(err);
                        showError(err);
                    }
                }
            }
        } catch (err: unknown) {
            console.error("Error polling session status", err);
        }
    };

    const handleExecute = async (): Promise<void> => {
        clearPolling();
        setIsLoading(true);
        setIsPolling(false);
        setPollCount(0);
        setErrorMessage(null);
        setResultData(null);

        try {
            const res = await aiToolsApi.fetchUserStories({
                command,
                prompt,
            });

            const data: UserStoriesResult | undefined = res?.data;

            if (data) {
                setResultData(data);

                if (data.status === "COMPLETED") {
                    setIsLoading(false);
                    if (res?.message) {
                        showSuccess(res.message);
                    }
                    return;
                }

                if (data.sessionId) {
                    setIsPolling(true);
                    const sid = data.sessionId;

                    pollingIntervalRef.current = setInterval(() => {
                        checkSessionStatus(sid);
                    }, 3000);
                } else {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        } catch (err: unknown) {
            setIsLoading(false);
            setIsPolling(false);
            const serverMsg = (
                err as { response?: { data?: { message?: string } } }
            )?.response?.data?.message;
            const fallbackMsg = (err as { message?: string })?.message;
            const msg =
                serverMsg ||
                fallbackMsg ||
                t("tools.user_stories.error_occurred");
            setErrorMessage(msg);
            showError(msg);
        }
    };

    const handleClose = (): void => {
        if (!isLoading && !isPolling) {
            clearPolling();
            onClose();
        }
    };

    const handleCopy = async (): Promise<void> => {
        if (!resultData?.result) {
            return;
        }

        try {
            await navigator.clipboard.writeText(resultData.result);
            setCopied(true);
            showSuccess(t("tools.user_stories.copied"));
            setTimeout(() => {
                setCopied(false);
            }, 2000);
        } catch (e: unknown) {
            console.error("Failed to copy to clipboard", e);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t("tools.user_stories.modal_title")}
            subtitle={t("tools.user_stories.modal_subtitle")}
            maxWidth="3xl"
            hideFooter
        >
            <div className="flex flex-col space-y-5">
                <Alert variant="info">
                    <div className="flex flex-col space-y-1">
                        <span className="font-semibold text-xs">
                            {t("tools.user_stories.preview_notice_title")}
                        </span>
                        <span className="text-xs opacity-90">
                            {t("tools.user_stories.preview_notice_desc")}
                        </span>
                    </div>
                </Alert>

                <div className="w-full">
                    <Select
                        label={t("tools.user_stories.command_label")}
                        value={command}
                        options={commandOptions}
                        onChange={(val: string) => {
                            setCommand(val);
                        }}
                        searchable
                        disabled={isLoading || isPolling}
                    />
                </div>

                <div className="w-full">
                    <Textarea
                        label={t("tools.user_stories.prompt_label")}
                        placeholder={t("tools.user_stories.prompt_placeholder")}
                        value={prompt}
                        onChange={(
                            e: React.ChangeEvent<HTMLTextAreaElement>
                        ) => {
                            setPrompt(e.target.value);
                        }}
                        rows={3}
                        disabled={isLoading || isPolling}
                    />
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isLoading || isPolling}
                    >
                        {t("tools.user_stories.close")}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleExecute}
                        disabled={isLoading || isPolling || !prompt.trim()}
                        isLoading={isLoading || isPolling}
                    >
                        {isLoading || isPolling
                            ? t("tools.user_stories.querying_button")
                            : t("tools.user_stories.query_button")}
                    </Button>
                </div>

                {errorMessage ? (
                    <Alert variant="danger">
                        <span className="text-xs">{errorMessage}</span>
                    </Alert>
                ) : null}

                {isPolling && resultData?.sessionId ? (
                    <div className="p-6 border border-amber-200 dark:border-amber-800/50 rounded-xl flex flex-col items-center justify-center space-y-3 bg-amber-50/40 dark:bg-amber-950/20 text-center">
                        <div className="flex items-center gap-2">
                            <Icon
                                value="loader2"
                                size={24}
                                className="animate-spin text-amber-600 dark:text-amber-400"
                            />
                            <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">
                                {t("tools.user_stories.polling_in_progress")}
                            </span>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <span className="text-[11px] font-mono text-slate-500 dark:text-zinc-400">
                                {t("tools.user_stories.session_created", {
                                    sessionId: resultData.sessionId,
                                })}
                            </span>
                            <span className="text-xs text-amber-700 dark:text-amber-300">
                                {t("tools.user_stories.polling_attempt", {
                                    count: pollCount,
                                })}
                            </span>
                        </div>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                                if (resultData?.sessionId) {
                                    checkSessionStatus(resultData.sessionId);
                                }
                            }}
                        >
                            <span className="text-xs">
                                {t("tools.user_stories.check_status_now")}
                            </span>
                        </Button>
                    </div>
                ) : null}

                {!isPolling && isLoading && !resultData?.sessionId ? (
                    <div className="p-8 border border-dashed border-slate-200 dark:border-zinc-800 rounded-xl flex flex-col items-center justify-center space-y-3 bg-slate-50/50 dark:bg-zinc-900/40">
                        <Icon
                            value="loader2"
                            size={32}
                            className="animate-spin text-primary"
                        />
                        <span className="text-xs text-slate-500 dark:text-zinc-400">
                            {t("tools.user_stories.querying_button")}
                        </span>
                    </div>
                ) : null}

                {!isPolling &&
                resultData &&
                resultData.status === "COMPLETED" ? (
                    <div className="border border-slate-200 dark:border-zinc-800 rounded-xl overflow-hidden bg-white dark:bg-zinc-900 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-slate-50 dark:bg-zinc-800/60 border-b border-slate-200 dark:border-zinc-800">
                            <div className="flex items-center gap-2">
                                <Icon
                                    value="sparkles"
                                    size={16}
                                    color="primary"
                                />
                                <span className="text-xs font-semibold text-slate-800 dark:text-zinc-100">
                                    {t("tools.user_stories.results_title")}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                {resultData.llmModel ? (
                                    <Badge variant="neutral" pill={false}>
                                        {resultData.llmModel}
                                    </Badge>
                                ) : null}
                                {resultData.status ? (
                                    <Badge variant="success" pill={false}>
                                        {resultData.status}
                                    </Badge>
                                ) : null}
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleCopy}
                                >
                                    <div className="flex items-center gap-1.5 text-xs">
                                        <Icon
                                            value={copied ? "check" : "copy"}
                                            size={14}
                                        />
                                        <span>
                                            {copied
                                                ? t("tools.user_stories.copied")
                                                : t(
                                                      "tools.user_stories.copy_result"
                                                  )}
                                        </span>
                                    </div>
                                </Button>
                            </div>
                        </div>

                        {resultData.sessionId ? (
                            <div className="px-4 py-2 border-b border-slate-100 dark:border-zinc-800 text-[11px] text-slate-500 dark:text-zinc-400 font-mono">
                                <span>
                                    {t("tools.user_stories.session_id")}:{" "}
                                    {resultData.sessionId}
                                </span>
                            </div>
                        ) : null}

                        <div className="p-4 max-h-[420px] overflow-y-auto text-sm text-slate-800 dark:text-zinc-200">
                            {resultData.result ? (
                                <Markdown>{resultData.result}</Markdown>
                            ) : (
                                <span className="text-xs italic text-slate-400 dark:text-zinc-500">
                                    {t("tools.user_stories.empty_state")}
                                </span>
                            )}
                        </div>
                    </div>
                ) : null}

                {!isLoading && !isPolling && !resultData && !errorMessage ? (
                    <div className="p-8 border border-dashed border-slate-200 dark:border-zinc-800 rounded-xl flex flex-col items-center justify-center text-center space-y-2 bg-slate-50/30 dark:bg-zinc-900/20">
                        <Icon
                            value="sparkles"
                            size={28}
                            className="text-slate-300 dark:text-zinc-600 mb-1"
                        />
                        <span className="text-xs text-slate-500 dark:text-zinc-400 max-w-sm">
                            {t("tools.user_stories.empty_state")}
                        </span>
                    </div>
                ) : null}
            </div>
        </Modal>
    );
};
